import React, { useState, useEffect, useCallback } from "react";
import { useForm, Controller, FormProvider, useFormContext } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
    Box, Typography, Stepper, Step, StepLabel, Button,
    Avatar, CircularProgress, IconButton, Switch,
    Alert,
    AlertTitle
} from "@mui/material";
import {
    CloudUpload, CheckCircle, Delete, ArrowBack, ArrowForward,
    Visibility, VisibilityOff, InsertDriveFileOutlined,
    CopyAll, Check, Autorenew, Close as CloseIcon,
    Image as ImageIcon, Videocam,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { styled } from "@mui/material/styles";
import { StepConnector, stepConnectorClasses } from "@mui/material";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import api from "../../../services/Axios";
import { CreatorCredentialsCard } from "./CreatorCredentialsCard";
import {
    CREATOR_PASSWORD_PATTERN,
    generateCreatorPassword,
    rememberCreatorPassword,
    fanAppMagicLink,
    creatorDisplayName,
} from "./creatorCredentials";
import Slider from "@mui/material/Slider";

const STEPS = ["Personal Details", "Media", "AI Configuration", "System Prompt & Memory"];

const ELEVENLABS_MODELS = [
    { value: "eleven_v4_turbo", label: "Eleven v4 turbo" },
    { value: "eleven_flash_v2_5", label: "Eleven Flash 2.5" },
    { value: "eleven_multilingual_v2", label: "Multilingual v2" },
];
const ELEVENLABS_OUTPUT_FORMATS = [
    { value: "mp3_44100_128", label: "MP3 44.1kHz (128kbps)" },
    { value: "mp3_44100_192", label: "MP3 44.1kHz (192kbps)" },
    { value: "pcm_16000", label: "PCM 16kHz" },
    { value: "pcm_22050", label: "PCM 22.05kHz" },
    { value: "pcm_24000", label: "PCM 24kHz" },
    { value: "pcm_44100", label: "PCM 44.1kHz" },
    { value: "ulaw_8000", label: "μ-law 8kHz" },
];
const AVATAR_PROVIDERS = [
    { value: "heygen", label: "HeyGen" },
    { value: "tavus", label: "Tavus" },
];
const GUARDRAIL_TOGGLES = [
    { name: "explicit", label: "Explicit" },
    { name: "politics", label: "Politics" },
    { name: "relationship_status", label: "Relationship Status" },
];

// Fields whose changes should trigger the AI-config save on step 3.
const AI_CONFIG_FIELDS = [
    "avatarProvider", "heygenAvatarId", "elevenlabsVoiceId", "elevenlabsModel",
    "stability", "similarityBoost", "style", "speed", "panning",
    "useSpeakerBoost", "languageOverride", "language", "audioEffects", "outputFormat",
];

function extractErrorMessage(err, fallback) {
    const data = err?.response?.data;
    if (Array.isArray(data?.detail)) {
        return data.detail.map((d) => `${(d.loc || []).slice(1).join(".")}: ${d.msg}`).join(" | ");
    }
    if (typeof data?.detail === "string") return data.detail;
    if (data?.errors) return data.errors.map((e) => e.long_message || e.message).join(" | ");
    if (data?.message) return data.message;
    return fallback;
}

// ─────────────────────────────────────────────────────────────────
// Validation schema — depends on whether we're creating (password
// required) or editing (password step is skipped entirely).
// ─────────────────────────────────────────────────────────────────
function buildSchema(isEditMode) {
    return z.object({
        // Step 1
        firstName: z.string().min(1, "First name is required"),
        lastName: z.string().min(1, "Last name is required"),
        email: z.string().min(1, "Email is required").email("Enter a valid email"),
        phone: z.string().optional(),
        username: z.string().min(1, "Username is required"),
        revenueSplit: z.coerce
            .number({ invalid_type_error: "Revenue split is required" })
            .min(0, "Must be between 0 and 100")
            .max(100, "Must be between 0 and 100"),
        // password: isEditMode
        //     ? z.string().optional()
        //     : z.string().regex(
        //         CREATOR_PASSWORD_PATTERN,
        //         "Must be at least 7 characters with a digit, a special character (!@#$%^&*), a lowercase letter, and an uppercase letter"
        //     ),

        // Step 2 — skippable
        profilePicture: z.instanceof(File).nullable().optional(),
        splashVideo: z.union([z.instanceof(File), z.string()]).nullable().optional(),

        // Step 3
        avatarProvider: z.union([z.enum(["heygen", "tavus"]), z.literal("")]).optional(),
        // heygenAvatarId: z.string().min(1, "HeyGen Avatar ID is required"),
        heygenAvatarId: z.string().optional(),
        elevenlabsVoiceId: z.string().optional(),
        elevenlabsModel: z.string().min(1, "Select an ElevenLabs model"),
        stability: z.coerce.number({ invalid_type_error: "Stability is required" }).min(0, "Must be between 0 and 1").max(1, "Must be between 0 and 1"),
        similarityBoost: z.coerce.number({ invalid_type_error: "Similarity boost is required" }).min(0, "Must be between 0 and 1").max(1, "Must be between 0 and 1"),
        style: z.coerce.number({ invalid_type_error: "Style is required" }).min(0, "Must be between 0 and 1").max(1, "Must be between 0 and 1"),
        speed: z.coerce.number({ invalid_type_error: "Speed is required" }).min(0, "Must be 0 or more"),
        useSpeakerBoost: z.boolean().optional(),
        languageOverride: z.boolean().optional(),
        language: z.string().optional(),
        audioEffects: z.boolean().optional(),
        outputFormat: z.string().min(1, "Select an output format"),
        guardrails: z.object({
            explicit: z.boolean().optional(),
            politics: z.boolean().optional(),
            relationship_status: z.boolean().optional(),
        }),

        // Step 4
        systemPrompt: z.string().min(1, "System prompt is required"),
        memoryFile: z.any().nullable().optional(),
    });
}

const stepFields = (isEditMode) => [
    ["firstName", "lastName", "email", "username", "revenueSplit"],
    ["splashVideo"],
    ["avatarProvider", "heygenAvatarId", "elevenlabsVoiceId", "elevenlabsModel", "stability", "similarityBoost", "style", "speed", "panning", "outputFormat"],
    ["systemPrompt"],
];

function SliderField({ label, name, control, error, min = 0, max = 1, step = 0.01, leftLabel, rightLabel }) {
    return (
        <div className="co-field">
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                <span className="co-label">{label}</span>
            </Box>
            <Controller
                name={name}
                control={control}
                render={({ field: { value, onChange } }) => (
                    <>
                        <Box display="flex" alignItems="center" gap={2}>
                            <Box flex={1}>
                                <Box display="flex" justifyContent="space-between" mb={0.5}>
                                    <span style={{ fontSize: 12, color: "#9ca3af" }}>{leftLabel}</span>
                                    <span style={{ fontSize: 12, color: "#9ca3af" }}>{rightLabel}</span>
                                </Box>
                                <Slider
                                    value={typeof value === "number" ? value : Number(value) || 0}
                                    onChange={(_, v) => onChange(v)}
                                    min={min}
                                    max={max}
                                    step={step}
                                    sx={{
                                        color: "#A855F7",
                                        height: 4,
                                        "& .MuiSlider-thumb": {
                                            width: 16,
                                            height: 16,
                                            backgroundColor: "#fff",
                                            border: "3px solid #A855F7",
                                            boxShadow: "none",
                                            "&:hover, &.Mui-focusVisible": {
                                                boxShadow: "0 0 0 6px rgba(168, 85, 247, 0.16)",
                                            },
                                        },
                                        "& .MuiSlider-rail": { opacity: 1, backgroundColor: "#e5e7eb" },
                                    }}
                                />
                            </Box>
                            <Box
                                sx={{
                                    minWidth: 48,
                                    textAlign: "center",
                                    fontSize: 13,
                                    fontWeight: 600,
                                    color: "#12131A",
                                    border: "1px solid #e0e0e6",
                                    borderRadius: 1.5,
                                    py: 0.5,
                                }}
                            >
                                {(typeof value === "number" ? value : Number(value) || 0).toFixed(2)}
                            </Box>
                        </Box>
                    </>
                )}
            />
            {error && <span className="co-error-text">{error}</span>}
        </div>
    );
}
function FormField({ label, disabled, hint, error, prefix, register, name, registerOptions, ...rest }) {
    return (
        <div className="co-field">
            {label && (
                <Box display="flex" justifyContent="space-between">
                    <span className="co-label">{label}</span>
                    {hint && <span className="co-hint">{hint}</span>}
                </Box>
            )}
            <Box className={prefix ? "co-amount-wrap" : undefined}>
                {prefix && <span className="co-amount-prefix">{prefix}</span>}
                <input
                    disabled={disabled}
                    className={`form-control co-input${error ? " co-error" : ""}`}
                    {...(register ? register(name, registerOptions) : {})}
                    {...rest}
                />
            </Box>
            {error && <span className="co-error-text">{error}</span>}
        </div>
    );
}
// ─────────────────────────────────────────────────────────────────
// Custom stepper visuals — gradient connector + numbered/check circles
// ─────────────────────────────────────────────────────────────────
const GradientConnector = styled(StepConnector)(() => ({
    [`&.${stepConnectorClasses.alternativeLabel}`]: {
        top: 18,
        left: "calc(-50% + 18px)",
        right: "calc(50% + 18px)",
    },
    [`&.${stepConnectorClasses.active} .${stepConnectorClasses.line}`]: {
        backgroundImage: "linear-gradient(90deg, #FF2D92, #A855F7)",
    },
    [`&.${stepConnectorClasses.completed} .${stepConnectorClasses.line}`]: {
        backgroundImage: "linear-gradient(90deg, #FF2D92, #A855F7)",
    },
    [`& .${stepConnectorClasses.line}`]: {
        height: 3,
        border: 0,
        borderRadius: 2,
        backgroundColor: "#e5e7eb",
        transition: "background-image 0.3s ease",
    },
}));

const StepIconRoot = styled("div")(({ ownerState }) => ({
    width: 36,
    height: 36,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
    fontWeight: 700,
    transition: "all 0.25s ease",
    ...(ownerState.active && {
        backgroundImage: "linear-gradient(90deg, #FF2D92, #A855F7)",
        color: "#fff",
        boxShadow: "0 0 0 4px rgba(168, 85, 247, 0.15)",
    }),
    ...(ownerState.completed && {
        backgroundImage: "linear-gradient(90deg, #FF2D92, #A855F7)",
        color: "#fff",
    }),
    ...(!ownerState.active && !ownerState.completed && {
        backgroundColor: "#fff",
        color: "#9ca3af",
        border: "2px solid #e5e7eb",
    }),
}));

function GradientStepIcon({ active, completed, icon }) {
    return (
        <StepIconRoot ownerState={{ active, completed }}>
            {completed ? <Check sx={{ fontSize: 18 }} /> : icon}
        </StepIconRoot>
    );
}
// ─────────────────────────────────────────────────────────────────
// Step 1 — Personal details
// ─────────────────────────────────────────────────────────────────
function StepPersonalDetails({ isEditMode, loadingIdentity, submitError }) {
    const { register, control, setValue,getValues, formState: { errors } } = useFormContext();
    const [showPassword, setShowPassword] = useState(!isEditMode);

    const handleGeneratePassword = () => {
        setValue("password", generateCreatorPassword(), { shouldValidate: true, shouldDirty: true });
        setShowPassword(true);
    };

    const generateEmail = useCallback(() => {
        const generatedEmail =getValues("username") && getValues("username") + "@tlknow.ai";
        setValue("email", generatedEmail, { shouldValidate: true, shouldDirty: true });
    }, [getValues, setValue]);


    if (loadingIdentity) {
        return (
            <Box display="flex" justifyContent="center" py={6}>
                <CircularProgress size={28} />
            </Box>
        );
    }

    return (
        <Box display="flex" flexDirection="column" gap={2.5}>
            <Box display="flex" gap={2}>
                <FormField disabled={isEditMode} label="FIRST NAME" register={register} name="firstName" error={errors.firstName?.message} autoComplete="off" />
                <FormField disabled={isEditMode} label="LAST NAME" register={register} name="lastName" error={errors.lastName?.message} autoComplete="off" />
            </Box>
            <FormField disabled={isEditMode} label="USERNAME"
            onKeyUp={generateEmail}
            register={register} name="username" error={errors.username?.message} autoComplete="off" />
            <FormField  disabled={true} label="EMAIL ADDRESS" type="email" register={register} name="email" error={errors.email?.message} autoComplete="off" />

            
            {(submitError)?.includes("Username") && (
                <Typography color="error" variant="body2">
                    {submitError}
                </Typography>
            )}
            {/* <Box className="co-field">
                <span className="co-label">PHONE NUMBER</span>
                <Controller
                    name="phone"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                        <PhoneInput
                            country="in"
                            value={value}
                            onChange={onChange}
                            inputStyle={{ width: "100%", height: 44, fontSize: 14, borderRadius: 8, borderColor: "#e0e0e6" }}
                            buttonStyle={{ borderRadius: "8px 0 0 8px", borderColor: "#e0e0e6" }}
                            dropdownStyle={{ fontSize: 14 }}
                        />
                    )}
                />
            </Box> */}

            <FormField
                label="CREATOR REVENUE SPLIT %"
                type="number"
                step="0.1"
                min="0"
                max="100"
                register={register}
                name="revenueSplit"
                error={errors.revenueSplit?.message}
            />

            {/* {!isEditMode && (
                <div className="co-field">
                    <span className="co-label">PASSWORD</span>
                    <Box display="flex" gap={1} alignItems="stretch">
                        <Box position="relative" flex={1}>
                            <input
                                className={`co-input${errors.password ? " co-error" : ""}`}
                                type={showPassword ? "text" : "password"}
                                autoComplete="new-password"
                                style={{ paddingRight: 36 }}
                                {...register("password")}
                            />
                            <IconButton
                                size="small"
                                onClick={() => setShowPassword((s) => !s)}
                                sx={{ position: "absolute", right: 4, top: "50%", transform: "translateY(-50%)" }}
                            >
                                {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                            </IconButton>
                        </Box>
                        <Button
                            type="button"
                            variant="outlined"
                            onClick={handleGeneratePassword}
                            startIcon={<Autorenew fontSize="small" />}
                            sx={{
                                textTransform: "none",
                                fontWeight: 600,
                                borderRadius: 2,
                                px: 2,
                                color: "#374151",
                                borderColor: "#E5E7EB",
                                whiteSpace: "nowrap",
                                "&:hover": { borderColor: "#D1D5DB", background: "#F9FAFB" },
                            }}
                        >
                            Generate
                        </Button>
                    </Box>
                    {errors.password && <span className="co-error-text">{errors.password.message}</span>}
                </div>
            )} */}
        </Box>
    );
}

// ─────────────────────────────────────────────────────────────────
// Step 2 — Media. Uploads immediately on file select (needs userId).
// ─────────────────────────────────────────────────────────────────
function formatBytes(bytes) {
    if (!bytes && bytes !== 0) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function UploadDropzone({ icon, title, subtitle, accept, disabled, onFile }) {
    return (
        <Box
            component="label"
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.5,
                py: 5,
                px: 3,
                border: "1.5px dashed #d9d9e2",
                borderRadius: 3,
                cursor: disabled ? "not-allowed" : "pointer",
                opacity: disabled ? 0.6 : 1,
                bgcolor: "#fafafc",
                transition: "border-color 0.2s ease, background 0.2s ease",
                "&:hover": disabled ? {} : { borderColor: "#A855F7", bgcolor: "#fdf4ff" },
            }}
        >
            <Box
                sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "1px solid #e0e0e6",
                    bgcolor: "#fff",
                }}
            >
                {icon}
            </Box>
            <Typography fontSize={14} fontWeight={600} color="#12131A">
                {title}
            </Typography>
            <Typography fontSize={12} color="#9ca3af">
                {subtitle}
            </Typography>
            <input type="file" accept={accept} hidden disabled={disabled} onChange={(e) => onFile(e.target.files?.[0] || null)} />
        </Box>
    );
}

function UploadFileCard({ icon, name, sizeLabel, progress, isUploading, isComplete, onDelete, preview }) {
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                border: "1px solid #eceff1",
                borderRadius: 2.5,
                p: 1.5,
                bgcolor: "#fff",
            }}
        >
            <Box
                sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1.5,
                    flexShrink: 0,
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "#f5f0ff",
                }}
            >
                {preview ? (
                    <Box component="img" src={preview} sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                ) : (
                    icon
                )}
            </Box>

            <Box flex={1} minWidth={0}>
                <Typography fontSize={13} fontWeight={600} color="#12131A" noWrap>
                    {name}
                </Typography>
                <Box display="flex" alignItems="center" gap={0.75} mt={0.25}>
                    {sizeLabel && (
                        <Typography fontSize={12} color="#9ca3af">
                            {sizeLabel}
                        </Typography>
                    )}
                    {sizeLabel && <Typography fontSize={12} color="#d1d5db">•</Typography>}
                    {isComplete && (
                        <Box display="flex" alignItems="center" gap={0.5}>
                            <CheckCircle sx={{ fontSize: 14, color: "#22c55e" }} />
                            <Typography fontSize={12} color="#22c55e" fontWeight={600}>Completed</Typography>
                        </Box>
                    )}
                    {isUploading && (
                        <Box display="flex" alignItems="center" gap={0.5}>
                            <CircularProgress size={12} sx={{ color: "#A855F7" }} />
                            <Typography fontSize={12} color="#A855F7" fontWeight={600}>Uploading...</Typography>
                        </Box>
                    )}
                </Box>

                {isUploading && (
                    <Box display="flex" alignItems="center" gap={1} mt={1}>
                        <Box flex={1} sx={{ height: 5, borderRadius: 3, bgcolor: "#eceff1", overflow: "hidden" }}>
                            <Box
                                sx={{
                                    height: "100%",
                                    width: `${progress}%`,
                                    borderRadius: 3,
                                    background: "linear-gradient(90deg, #FF2D92, #A855F7)",
                                    transition: "width 0.2s ease",
                                }}
                            />
                        </Box>
                        <Typography fontSize={12} fontWeight={600} color="#12131A" minWidth={32} textAlign="right">
                            {progress}%
                        </Typography>
                    </Box>
                )}
            </Box>

            {onDelete && (
                <IconButton size="small" onClick={onDelete}>
                    {isUploading ? <CloseIcon fontSize="small" /> : <Delete fontSize="small" sx={{ color: "#9ca3af" }} />}
                </IconButton>
            )}
        </Box>
    );
}
function StepMedia({ userId, mediaUrls, setMediaUrls }) {
    const { watch, setValue, formState: { errors } } = useFormContext();
    const profilePicture = watch("profilePicture");
    const splashVideo = watch("splashVideo");
    const [uploading, setUploading] = useState({ profile_picture: false, splash_video: false });
    const [uploadProgress, setUploadProgress] = useState({ profile_picture: 0, splash_video: 0 });
    const [uploadError, setUploadError] = useState("");
    const [localPreviews, setLocalPreviews] = useState({ profile_picture: null, splash_video: null });

    // Create/revoke object URLs for local File previews so we never hand a File to <img>/<video>.
    useEffect(() => {
        if (profilePicture instanceof File) {
            const url = URL.createObjectURL(profilePicture);
            setLocalPreviews((p) => ({ ...p, profile_picture: url }));
            return () => URL.revokeObjectURL(url);
        }
        setLocalPreviews((p) => ({ ...p, profile_picture: null }));
    }, [profilePicture]);

    useEffect(() => {
        if (splashVideo instanceof File) {
            const url = URL.createObjectURL(splashVideo);
            setLocalPreviews((p) => ({ ...p, splash_video: url }));
            return () => URL.revokeObjectURL(url);
        }
        setLocalPreviews((p) => ({ ...p, splash_video: null }));
    }, [splashVideo]);

    const uploadMedia = async (file, kind) => {
        setUploadError("");
        setUploading((u) => ({ ...u, [kind]: true }));
        setUploadProgress((p) => ({ ...p, [kind]: 0 }));
        try {
            const formData = new FormData();
            formData.append("file", file);
            formData.append("kind", kind);
            const res = await api.post(`/admin/creators/${userId}/media`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
                onUploadProgress: (evt) => {
                    if (!evt.total) return;
                    const pct = Math.round((evt.loaded / evt.total) * 100);
                    setUploadProgress((p) => ({ ...p, [kind]: pct }));
                },
            });
            setMediaUrls((prev) => ({ ...prev, [kind]: res.data?.url }));
            if (kind === "profile_picture") {
                setValue("profilePicture", res.data?.url);
            } else {
                setValue("splashVideo", res.data?.url);
            }
        } catch (err) {
            console.error("Media upload failed:", err?.response?.status, err?.response?.data);
            setUploadError(extractErrorMessage(err, "Upload failed. Please try again."));
        } finally {
            setUploading((u) => ({ ...u, [kind]: false }));
            setUploadProgress((p) => ({ ...p, [kind]: 0 }));
        }
    };

    const handleDelete = (kind) => {
        setValue(kind === "profile_picture" ? "profilePicture" : "splashVideo", null);
        setMediaUrls((prev) => ({ ...prev, [kind]: null }));
    };

    // Prefer the local object-URL while a File is selected/uploading; fall back to the server URL.
    const picPreview = localPreviews.profile_picture || (typeof profilePicture === "string" ? profilePicture : mediaUrls.profile_picture);
    const videoPreview = localPreviews.splash_video || (typeof splashVideo === "string" ? splashVideo : mediaUrls.splash_video);

    return (
        <Box display="flex" flexDirection="column" gap={4}>
            {!userId && (
                <Typography fontSize={13} color="#d32f2f">
                    Personal details must be saved before media can be uploaded.
                </Typography>
            )}

            <Box>
                <Typography fontSize={13} fontWeight={700} mb={1.5} color="#12131A">PROFILE PICTURE</Typography>

                {!picPreview && (
                    <UploadDropzone
                        icon={<ImageIcon sx={{ fontSize: 20, color: "#A855F7" }} />}
                        title={uploading.profile_picture ? "Uploading..." : "Choose a photo or drag & drop it here"}
                        subtitle="JPG, PNG — max 10 MB"
                        accept="image/*"
                        disabled={!userId || uploading.profile_picture}
                        onFile={(file) => {
                            if (!file) return;
                            setValue("profilePicture", file);
                            uploadMedia(file, "profile_picture");
                        }}
                    />
                )}

                {(picPreview || uploading.profile_picture) && (
                    <UploadFileCard
                        icon={<ImageIcon sx={{ fontSize: 20, color: "#A855F7" }} />}
                        preview={picPreview}
                        name={profilePicture instanceof File ? profilePicture.name : "Profile picture"}
                        sizeLabel={profilePicture instanceof File ? formatBytes(profilePicture.size) : null}
                        progress={uploadProgress.profile_picture}
                        isUploading={uploading.profile_picture}
                        isComplete={!uploading.profile_picture && Boolean(picPreview)}
                        onDelete={() => handleDelete("profile_picture")}
                    />
                )}
            </Box>

            <Box>
                <Typography fontSize={13} fontWeight={700} mb={1.5} color="#12131A">SPLASH VIDEO</Typography>

                {videoPreview && !uploading.splash_video && (
                    <Box mb={1.5}>
                        <video src={videoPreview} controls style={{ width: "100%", maxHeight: 220, borderRadius: 12, background: "#000" }} />
                    </Box>
                )}

                {!videoPreview && (
                    <UploadDropzone
                        icon={<Videocam sx={{ fontSize: 20, color: "#A855F7" }} />}
                        title={uploading.splash_video ? "Uploading..." : "Choose a video or drag & drop it here"}
                        subtitle="MP4, MOV — max 500 MB"
                        accept="video/*"
                        disabled={!userId || uploading.splash_video}
                        onFile={(file) => {
                            if (!file) return;
                            setValue("splashVideo", file);
                            uploadMedia(file, "splash_video");
                        }}
                    />
                )}

                {(videoPreview || uploading.splash_video) && (
                    <UploadFileCard
                        icon={<Videocam sx={{ fontSize: 20, color: "#A855F7" }} />}
                        name={splashVideo instanceof File ? splashVideo.name : "Splash video"}
                        sizeLabel={splashVideo instanceof File ? formatBytes(splashVideo.size) : null}
                        progress={uploadProgress.splash_video}
                        isUploading={uploading.splash_video}
                        isComplete={!uploading.splash_video && Boolean(videoPreview)}
                        onDelete={() => handleDelete("splash_video")}
                    />
                )}
            </Box>

            {(uploadError || errors.splashVideo) && (
                <Typography fontSize={12} color="error">{uploadError || errors.splashVideo?.message}</Typography>
            )}
        </Box>
    );
}

// ─────────────────────────────────────────────────────────────────
// Step 3 — AI configuration. Voice id/model/tuning all live under one
// "ElevenLabs Voice Settings" heading; guardrails are separate toggles.
// ─────────────────────────────────────────────────────────────────
function StepAiConfig() {
    const { register, control, watch, formState: { errors } } = useFormContext();
    const avatarProvider = watch("avatarProvider");
    const avatarIdLabel = avatarProvider === "tavus"
        ? "TAVUS PAL ID (e.g. p3c963ewqi71)"
        : "HEYGEN AVATAR ID (e.g. 6789b9a9-0759-31z5-jk1d-e0b214c863fe)";
    // const languageOverrideEnabled = watch("languageOverride");
    return (
        <Box display="flex" flexDirection="column" gap={3}>
            <Box>
                <Typography fontSize={15} fontWeight={800} mb={1.5} color="#12131A">VIDEO SETTINGS</Typography>
                <Box display="flex" flexDirection="column" gap={2.5}>
                    <div className="co-field">
                        <span className="co-label">AVATAR PROVIDER</span>
                        <select className={`form-control co-input${errors.avatarProvider ? " co-error" : ""}`} {...register("avatarProvider")}>
                            <option value="">None</option>
                            {AVATAR_PROVIDERS.map((p) => (
                                <option key={p.value} value={p.value}>{p.label}</option>
                            ))}
                        </select>
                        {errors.avatarProvider && <span className="co-error-text">{errors.avatarProvider.message}</span>}
                    </div>
                    {avatarProvider && (
                        <FormField label={avatarIdLabel} register={register} name="heygenAvatarId" error={errors.heygenAvatarId?.message} />
                    )}
                </Box>
            </Box>

            <Box>
                <Typography fontSize={15} fontWeight={800} mb={1.5} color="#12131A">ELEVENLABS VOICE SETTINGS</Typography>
                <Box display="flex" flexDirection="column" gap={2.5}>
                    <FormField label="ELEVENLABS VOICE ID" register={register} name="elevenlabsVoiceId" error={errors.elevenlabsVoiceId?.message} />

                    <div className="co-field">
                        <span className="co-label">ELEVENLABS MODEL</span>
                        <select className={`form-control co-input${errors.elevenlabsModel ? " co-error" : ""}`} {...register("elevenlabsModel")}>
                            <option value="" disabled>Select elevenlabs model</option>
                            {ELEVENLABS_MODELS.map((m) => (
                                <option key={m.value} value={m.value}>{m.label}</option>
                            ))}
                        </select>
                        {errors.elevenlabsModel && <span className="co-error-text">{errors.elevenlabsModel.message}</span>}
                    </div>

                    <SliderField
                        label="STABILITY"
                        name="stability"
                        control={control}
                        error={errors.stability?.message}
                        min={0} max={1} step={0.01}
                        leftLabel="More variable" rightLabel="More stable"
                    />

                    <SliderField
                        label="SIMILARITY BOOST"
                        name="similarityBoost"
                        control={control}
                        error={errors.similarityBoost?.message}
                        min={0} max={1} step={0.01}
                        leftLabel="Low" rightLabel="High"
                    />

                    <SliderField
                        label="STYLE"
                        name="style"
                        control={control}
                        error={errors.style?.message}
                        min={0} max={1} step={0.01}
                        leftLabel="None" rightLabel="Exaggerated"
                    />

                    <SliderField
                        label="SPEED"
                        name="speed"
                        control={control}
                        error={errors.speed?.message}
                        min={0} max={1} step={0.01}
                        leftLabel="Slower" rightLabel="Faster"
                    />

                    {/* <SliderField
                        label="PANNING"
                        name="panning"
                        control={control}
                        error={errors.panning?.message}
                        min={-1} max={1} step={0.01}
                        leftLabel="Left" rightLabel="Right"
                    />

                    <div className="co-field">
                        <span className="co-label">OUTPUT FORMAT</span>
                        <select className={`form-control co-input${errors.outputFormat ? " co-error" : ""}`} {...register("outputFormat")}>
                            <option value="">Select an output format</option>
                            {ELEVENLABS_OUTPUT_FORMATS.map((f) => (
                                <option key={f.value} value={f.value}>{f.label}</option>
                            ))}
                        </select>
                        {errors.outputFormat && <span className="co-error-text">{errors.outputFormat.message}</span>}
                    </div>

                    <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ py: 0.5 }}>
                        <span className="co-label" style={{ fontWeight: 600 }}>Language Override</span>
                        <Controller
                            name="languageOverride"
                            control={control}
                            render={({ field: { value, onChange } }) => (
                                <Switch checked={Boolean(value)} onChange={(e) => onChange(e.target.checked)} />
                            )}
                        />
                    </Box>
                    {languageOverrideEnabled && (
                        <FormField label="LANGUAGE CODE" placeholder="e.g. en, es, hi" register={register} name="language" error={errors.language?.message} />
                    )}

                    <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ py: 0.5 }}>
                        <span className="co-label" style={{ fontWeight: 600 }}>Audio Effects</span>
                        <Controller
                            name="audioEffects"
                            control={control}
                            render={({ field: { value, onChange } }) => (
                                <Switch checked={Boolean(value)} onChange={(e) => onChange(e.target.checked)} />
                            )}
                        />
                    </Box> */}
                    <label className="co-field" style={{ flexDirection: "row", alignItems: "center", gap: 10, cursor: "pointer" }}>
                        <input type="checkbox" className="co-checkbox" {...register("useSpeakerBoost")} />
                        <span className="co-label" style={{ fontWeight: 600 }}>Use Speaker Boost</span>
                    </label>

                </Box>
            </Box>

            <Box>
                <Typography fontSize={15} fontWeight={800} mb={1.5} color="#12131A">GUARDRAILS</Typography>
                <Box display="flex" flexDirection="column" gap={1}>
                    {GUARDRAIL_TOGGLES.map((g) => (
                        <Box key={g.name} display="flex" alignItems="center" justifyContent="space-between" sx={{ py: 0.5 }}>
                            <span className="co-label" style={{ fontWeight: 600 }}>{g.label}</span>
                            <Controller
                                name={`guardrails.${g.name}`}
                                control={control}
                                render={({ field: { value, onChange } }) => (
                                    <Switch checked={Boolean(value)} onChange={(e) => onChange(e.target.checked)} />
                                )}
                            />
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    );
}

// ─────────────────────────────────────────────────────────────────
// Step 4 — System prompt + memory (.md upload)
// ─────────────────────────────────────────────────────────────────
function StepPromptAndMemory({ userId, onPromptLoadingChange }) {
    const { control, watch, setValue, formState: { errors, dirtyFields } } = useFormContext();
    const memoryFile = watch("memoryFile");
    const systemPrompt = watch("systemPrompt");

    const [isUploadingMemory, setUploadingMemory] = useState(false);
    const [uploadError, setUploadError] = useState("");
    const [promptLoading, setPromptLoading] = useState(Boolean(userId));
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (!userId) {
            setPromptLoading(false);
            onPromptLoadingChange?.(false);
            return undefined;
        }
        let cancelled = false;
        setPromptLoading(true);
        onPromptLoadingChange?.(true);
        api.get(`/admin/creators/${userId}/prompt`)
            .then((res) => {
                if (cancelled) return;
                const text = res.data?.system_prompt || "";
                if (text && !dirtyFields.systemPrompt) {
                    setValue("systemPrompt", text, { shouldDirty: false, shouldValidate: true });
                }
            })
            .catch((err) => console.error("Failed to load Langfuse prompt:", err))
            .finally(() => {
                if (!cancelled) {
                    setPromptLoading(false);
                    onPromptLoadingChange?.(false);
                }
            });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId]);

    const handleCopyPrompt = async () => {
        if (!systemPrompt) return;
        try {
            await navigator.clipboard.writeText(systemPrompt);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch (err) {
            console.error("Copy to clipboard failed:", err);
        }
    };
    const handleUploadMemory = async (file) => {
        setUploadError("");
        setUploadingMemory(true);
        try {
            const formData = new FormData();
            formData.append("memory_file", file);
            const res = await api.post(`/admin/creators/${userId}/knowledge`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setValue("memoryFile", res.data);
        } catch (err) {
            console.error("Media upload failed:", err?.response?.status, err?.response?.data);
            setUploadError(extractErrorMessage(err, "Upload failed. Please try again."));
        } finally {
            setUploadingMemory(false);
        }
    }
    return (
        <Box display="flex" flexDirection="column" gap={3}>
            <Box>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Typography fontSize={13} fontWeight={700} color="#12131A">
                        SYSTEM PROMPT
                        {promptLoading ? " — loading from Langfuse..." : ""}
                    </Typography>
                    <Button
                        onClick={handleCopyPrompt}
                        disabled={!systemPrompt}
                        startIcon={copied ? <Check fontSize="small" /> : <CopyAll fontSize="small" />}
                        sx={{ textTransform: "none", borderRadius: 2, color: copied ? "#22c55e" : "#12131A" }}
                    >
                        {copied ? "Copied" : "Copy to clipboard"}
                    </Button>
                </Box>
                <Controller
                    name="systemPrompt"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                        <textarea
                            value={value}
                            onChange={onChange}
                            disabled={promptLoading}
                            placeholder={promptLoading ? "Loading prompt from Langfuse..." : "Write your system prompt here..."}
                            style={{
                                display: "block",
                                width: "100%",
                                height: "75vh",
                                minHeight: 480,
                                boxSizing: "border-box",
                                resize: "vertical",
                                border: "1px solid #e0e0e6",
                                borderRadius: 5,
                                padding: 12,
                                fontSize: 14,
                                lineHeight: 1.5,
                                fontFamily: "inherit",
                                background: promptLoading ? "#f7f7f9" : "#fff",
                            }}
                        />
                    )}
                />
                {errors.systemPrompt && (
                    <Typography fontSize={12} color="error" mt={0.5}>{errors.systemPrompt.message}</Typography>
                )}
            </Box>

            <Box>
                <Typography fontSize={13} fontWeight={700} mb={1} color="#12131A">MEMORY (.MD)</Typography>
                <Typography fontSize={12} color="#777" mb={1.5}>
                    Creator lore / background — markdown facts the AI can recall about this creator.
                </Typography>
                <Box display="flex" alignItems="center" gap={2}>
                    <Button
                        component="label"
                        variant="outlined"
                        disabled={isUploadingMemory}
                        startIcon={<InsertDriveFileOutlined />}
                        sx={{ textTransform: "none", borderRadius: 2, borderColor: "#e0e0e6", color: "#12131A" }}
                    >
                        {isUploadingMemory ? "Uploading..." : "Upload .md file"}
                        <input
                            type="file"
                            accept=".md,text/markdown"
                            hidden
                            onChange={(e) => handleUploadMemory(e.target.files[0])}
                        />
                    </Button>
                    {memoryFile && (
                        <>
                            <Typography fontSize={13} color="#555">{memoryFile?.title}</Typography>
                            <IconButton size="small" onClick={() => setValue("memoryFile", null)}>
                                <Delete fontSize="small" />
                            </IconButton>
                        </>
                    )}
                </Box>
                {uploadError && (
                    <Typography fontSize={12} color="error" mt={0.5}>{uploadError}</Typography>
                )}
            </Box>
        </Box>
    );
}

function SuccessScreen({ onDone, credentials }) {
    return (
        <Box textAlign="center" py={6}>
            <CheckCircle
                sx={{
                    fontSize: 64,
                    color: "#22c55e",
                    mb: 2,
                }}
            />

            <Typography
                variant="h5"
                fontWeight={700}
                color="#12131A"
                mb={1}
            >
                Congratulations!
            </Typography>

            <Typography
                fontSize={14}
                color="#555"
                mb={3}
            >
                Your creator has been onboarded successfully.
            </Typography>

            {/* Important credentials message */}
           {credentials && <Alert
                severity="warning"
                variant="outlined"
                sx={{
                    textAlign: "left",
                    mx: "auto",
                    mb: 3,
                    borderRadius: 2,
                    maxWidth: 520,
                    border: "1px solid #F59E0B",
                    backgroundColor: "#FFFBEB",
                    color: "#92400E",
                    "& .MuiAlert-icon": {
                        color: "#D97706",
                    },
                }}
            >
                <AlertTitle
                    sx={{
                        fontWeight: 700,
                        color: "#92400E",
                    }}
                >
                    Important: Save your credentials
                </AlertTitle>

                <Typography fontSize={14}>
                    Make sure to copy your login credentials for future reference.
                </Typography>
            </Alert>}

            {credentials && (
                <Box
                    textAlign="left"
                    mx="auto"
                    mb={3}
                    sx={{ maxWidth: 520 }}
                >
                    <CreatorCredentialsCard
                        credentials={credentials}
                    />
                </Box>
            )}

            <Button
                variant="contained"
                onClick={onDone}
                sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    px: 4,
                    borderRadius: 2,
                    background:
                        "linear-gradient(90deg, #FF2D92, #A855F7)",
                    "&:hover": {
                        background:
                            "linear-gradient(90deg, #E0227D, #9333EA)",
                    },
                }}
            >
                Go to Creators
            </Button>
        </Box>
    );
}

// ─────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────
export default function CreatorOnboarding() {
    const navigate = useNavigate();
    const location = useLocation();
    const initialUserId = location.state?.userId || null;
    const initialUsername = location.state?.username || null;
    const isEditMode = Boolean(initialUserId || initialUsername);

    const [activeStep, setActiveStep] = useState(0);
    const [userId, setUserId] = useState(initialUserId);
    const [mediaUrls, setMediaUrls] = useState({ profile_picture: null, splash_video: null });
    const [loadingIdentity, setLoadingIdentity] = useState(isEditMode);
    const [submitting, setSubmitting] = useState(false);
    const [creatingUser, setCreatingUser] = useState(false);
    const [stepBusy, setStepBusy] = useState(false);
    const [stepBusyLabel, setStepBusyLabel] = useState("Next");
    const [submitError, setSubmitError] = useState("");
    const [completed, setCompleted] = useState(false);
    const [subscriptionPlan, setSubscriptionPlan] = useState(false);
    const [createdCreds, setCreatedCreds] = useState(null);
    const [promptLoading, setPromptLoading] = useState(false);

    const methods = useForm({
        resolver: zodResolver(buildSchema(isEditMode)),
        mode: "onTouched",
        defaultValues: {
            firstName: "", lastName: "", email: "",
            username: "", revenueSplit: "0",
            profilePicture: null, splashVideo: null,
            avatarProvider: "",
            heygenAvatarId: "", elevenlabsVoiceId: "", elevenlabsModel: "",
            stability: "", similarityBoost: "", style: "", speed: "", panning: "",
            useSpeakerBoost: false, languageOverride: false, language: "", audioEffects: false,
            outputFormat: ELEVENLABS_OUTPUT_FORMATS[0].value,
            guardrails: { explicit: false, politics: false, relationship_status: false },
            systemPrompt: "", memoryFile: null,
        },
    });
    const { trigger, handleSubmit, reset, getValues, formState: { dirtyFields } } = methods;

    // Edit mode: resolve identity (and userId if only username was passed) then prefill step 1.
    useEffect(() => {
        if (!isEditMode) return;
        setLoadingIdentity(true);
        api.get(`/admin/creators/lookup?user_id=${initialUserId}`)
            .then((res) => {
                const match = (res.data || []).find((c) =>
                    initialUserId ? c.user_id === initialUserId : c.username === initialUsername
                );
                if (match) {
                    const voice = match.elevenlabs_voice_settings || {};
                    setUserId(match.user_id);
                    setMediaUrls({
                        profile_picture: match.profile_picture_url || null,
                        splash_video: match.splash_video_url || null,
                    });
                    setSubscriptionPlan(match?.subscription_plans_created);
                    reset({
                        ...getValues(),
                        firstName: match.first_name || "",
                        lastName: match.last_name || "",
                        email: match.email || "",
                        // phone: match.phone || "",
                        username: match.username || "",
                        revenueSplit: match.creator_split_percent ?? "",
                        profilePicture: match.profile_picture_url || null,
                        splashVideo: match.splash_video_url || null,
                        avatarProvider: match.avatar_provider || "",
                        heygenAvatarId: match.avatar_id || "",
                        elevenlabsVoiceId: match.voice_id || "",
                        elevenlabsModel: voice.model_id || "",
                        stability: voice.stability ?? "",
                        similarityBoost: voice.similarity_boost ?? "",
                        style: voice.style ?? "",
                        speed: voice.speed ?? "",
                        // panning: voice.panning ?? "",
                        useSpeakerBoost: Boolean(voice.use_speaker_boost),
                        // languageOverride: Boolean(voice.language_override),
                        // language: voice.language_code || "",
                        // audioEffects: Boolean(voice.audio_effects),
                        // outputFormat: voice.output_format || ELEVENLABS_OUTPUT_FORMATS[0].value,
                        guardrails: {
                            explicit: Boolean(match.guardrails?.explicit),
                            politics: Boolean(match.guardrails?.politics),
                            relationship_status: Boolean(match.guardrails?.relationship_status),
                        },
                        systemPrompt: match.system_prompt || "",
                        memoryFile: match.knowledge_title ? { "title": match.knowledge_title } : null,
                    });
                    // reset() above sets this data as the new dirty-tracking baseline —
                    // exactly what we want, since "changed" should mean "changed since load".
                }
            })
            .catch((err) => console.error("Failed to load creator identity:", err))
            .finally(() => setLoadingIdentity(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEditMode]);

    // Step 1 → create the user, then seed the rest of the form (AI config
    // defaults in particular) with whatever the create response returned,
    // so the AI Configuration step already reflects the server's defaults.
    const handleCreateCreator = async () => {
        setSubmitError("");
        setCreatingUser(true);
        const values = getValues();
        try {
            const res = await api.post("/admin/creators/create", {
                first_name: values.firstName || undefined,
                last_name: values.lastName || undefined,
                username: values.username || "",
                email_address: values.email,
                // phone_number: values.phone ? `+${values.phone}` : undefined,
                // password: values.password,
                creator_split_percent: values.revenueSplit,
                public_metadata: { role: "creator" },
            });
            const created = res.data;
            const newUserId = created?.user_id || created?.id;
            if (!newUserId) throw new Error("No user_id returned from creator creation.");
            const voice = created.elevenlabs_voice_settings || {};

            setUserId(newUserId);
            setCreatedCreds({
                displayName: creatorDisplayName({
                    first_name: created.first_name || values.firstName,
                    last_name: created.last_name || values.lastName,
                    username: created.username || values.username,
                }),
                username: created.username || values.username,
                email: created.email || values.email,
                // password: values.password,
                fanAppUrl: fanAppMagicLink(created.username || values.username),
            });
            // rememberCreatorPassword(newUserId, values.password);
            reset({
                ...values,
                revenueSplit: created.creator_split_percent ?? values.revenueSplit,
                avatarProvider: values.avatarProvider || "",
                heygenAvatarId: created.avatar_id || values.heygenAvatarId,
                elevenlabsVoiceId: created.voice_id || values.elevenlabsVoiceId,
                elevenlabsModel: voice.model_id || values.elevenlabsModel,
                stability: voice.stability ?? values.stability,
                similarityBoost: voice.similarity_boost ?? values.similarityBoost,
                style: voice.style ?? values.style,
                speed: voice.speed ?? values.speed,
                // panning: voice.panning ?? values.panning,
                useSpeakerBoost: voice.use_speaker_boost ?? values.useSpeakerBoost,
                // languageOverride: voice.language_override ?? values.languageOverride,
                // language: voice.language_code ?? values.language,
                // audioEffects: voice.audio_effects ?? values.audioEffects,
                // outputFormat: voice.output_format ?? values.outputFormat,
                guardrails: {
                    explicit: created.guardrails?.explicit ?? values.guardrails.explicit,
                    politics: created.guardrails?.politics ?? values.guardrails.politics,
                    relationship_status: created.guardrails?.relationship_status ?? values.guardrails.relationship_status,
                },
            });
            // Baseline for dirty-tracking is now the server's defaults — step 3
            // will only PATCH again if the user actually changes something from here.
            setActiveStep((s) => s + 1);
        } catch (err) {
            console.error("Create creator failed:", err?.response?.status, err?.response?.data);
            setSubmitError(extractErrorMessage(err, "Failed to create creator."));
        } finally {
            setCreatingUser(false);
        }
    };

    const handleNext = async () => {
        if (creatingUser || stepBusy) return;

        const valid = await trigger(stepFields(isEditMode)[activeStep]);
        if (!valid) return;

        if (activeStep === 0 && !isEditMode && !userId) {
            await handleCreateCreator();
            return;
        }

        if (activeStep === 0 && isEditMode) {
            // Only revenue split is editable on this step — only call the API if it changed.
            if (dirtyFields.revenueSplit) {
                const data = getValues();
                await onEditingRevenueSplit(data);
            }
            setActiveStep((s) => s + 1);
            return;
        }

        if (activeStep === 2) {
            const guardrailsDirty = Boolean(
                dirtyFields.guardrails && Object.values(dirtyFields.guardrails).some(Boolean)
            );
            const aiConfigDirty = AI_CONFIG_FIELDS.some((f) => dirtyFields[f]) || guardrailsDirty;

            setStepBusy(true);
            try {
                if (aiConfigDirty) {
                    setStepBusyLabel("Saving AI config...");
                    const data = getValues();
                    const ok = await onSubmitAIConfig(data);
                    if (!ok) return;
                }
                if (!subscriptionPlan) {
                    setStepBusyLabel("Creating subscriptions...");
                    const ok = await handleCreateSubscription();
                    if (!ok) return;
                }
                setActiveStep((s) => s + 1);
            } finally {
                setStepBusy(false);
                setStepBusyLabel("Next");
            }
            return;
        }

        setActiveStep((s) => s + 1);
    };

    const handlePrev = () => setActiveStep((s) => s - 1);

    // Field-level validation already ran via trigger() in handleNext — this
    // only handles the network call, and returns false so handleNext can
    // hold the user on the step if the save itself fails.
    const onSubmitAIConfig = async (data) => {
        setSubmitError("");
        try {
            const payload = {
                avatar_id: data.heygenAvatarId || null,
                avatar_provider: data.avatarProvider || null,
                voice_id: data.elevenlabsVoiceId || null,
                elevenlabs_voice_settings: {
                    model_id: data.elevenlabsModel,
                    stability: data.stability,
                    similarity_boost: data.similarityBoost,
                    style: data.style,
                    speed: data.speed,
                    // panning: data.panning,
                    use_speaker_boost: Boolean(data.useSpeakerBoost),
                    // language_override: Boolean(data.languageOverride),
                    // language_code: data.languageOverride ? data.language : undefined,
                    // audio_effects: Boolean(data.audioEffects),
                    // output_format: data.outputFormat,
                },
                guardrails: {
                    explicit: Boolean(data.guardrails?.explicit),
                    politics: Boolean(data.guardrails?.politics),
                    relationship_status: Boolean(data.guardrails?.relationship_status),
                },
            };
            await api.patch(`/admin/creators/${userId}/ai-config`, payload);
            return true;
        } catch (err) {
            console.error("Saving AI config failed:", err?.response?.status, err?.response?.data);
            setSubmitError(extractErrorMessage(err, "Failed to save AI configuration."));
            return false;
        }
    };

    const onEditingRevenueSplit = async (data) => {
        setSubmitError("");
        try {
            await api.patch(`/admin/creators/${userId}/split`, {
                creator_split_percent: data.revenueSplit == "" ? 0 : data.revenueSplit,
            });
        } catch (err) {
            console.error("Saving revenue split failed:", err?.response?.status, err?.response?.data);
            setSubmitError(extractErrorMessage(err, "Failed to save revenue split."));
        }
    };
    const handleCreateSubscription = async () => {
        setSubmitError("");
        try {
            await api.post(`/admin/creators/${userId}/setup-catalog`, {});
            setSubscriptionPlan(true);
            return true;
        } catch (err) {
            console.error("Create subscription failed:", err?.response?.status, err?.response?.data);
            setSubmitError(extractErrorMessage(err, "Failed to create subscription."));
            return false;
        }
    };
    const onFinalSubmit = async () => {
        const valid = await trigger(stepFields(isEditMode)[activeStep]);
        if (!valid) return;

        const data = getValues();
        setSubmitError("");
        setSubmitting(true);
        try {
            await api.put(`/admin/creators/${userId}/prompt`, { system_prompt: data.systemPrompt });
            setCompleted(true);
        } catch (err) {
            console.error("Creator onboarding failed:", err?.response?.status, err?.response?.data);
            setSubmitError(extractErrorMessage(err, "Something went wrong while onboarding this creator."));
        } finally {
            setSubmitting(false);
        }
    };

    if (completed) {
        return (
            <Box maxWidth={900} mx="auto" mt={6} px={2}>
                <SuccessScreen
                    onDone={() => navigate("/user/creators")}
                    credentials={isEditMode ? null : createdCreds}
                />
            </Box>
        );
    }

    return (
        <FormProvider {...methods}>
            <Box maxWidth={900} mx="auto" mt={4} px={2} pb={6}>
                <Typography variant="h5" fontWeight={700} color="#12131A" mb={3}>
                    {isEditMode ? "Edit Creator" : "Onboard Creator"}
                </Typography>

                <Stepper
                    activeStep={activeStep}
                    alternativeLabel
                    connector={<GradientConnector />}
                    sx={{ mb: 5 }}
                >
                    {STEPS.map((label) => (
                        <Step key={label}>
                            <StepLabel
                                StepIconComponent={GradientStepIcon}
                                sx={{
                                    "& .MuiStepLabel-label": {
                                        fontSize: 13,
                                        fontWeight: 600,
                                        color: "#9ca3af",
                                        mt: "10px !important",
                                        "&.Mui-active": { color: "#12131A", fontWeight: 700 },
                                        "&.Mui-completed": { color: "#12131A", fontWeight: 600 },
                                    },
                                }}
                            >
                                {label}
                            </StepLabel>
                        </Step>
                    ))}
                </Stepper>

                <Box sx={{ border: "1px solid #eceff1", borderRadius: 3, p: { xs: 2.5, sm: 5 }, bgcolor: "#fff" }}>
                    {activeStep === 0 && <StepPersonalDetails isEditMode={isEditMode} loadingIdentity={loadingIdentity} submitError={submitError} />}
                    {activeStep === 1 && <StepMedia userId={userId} mediaUrls={mediaUrls} setMediaUrls={setMediaUrls} />}
                    {activeStep === 2 && <StepAiConfig />}
                    {activeStep === 3 && (
                        <StepPromptAndMemory
                            userId={userId}
                            onPromptLoadingChange={setPromptLoading}
                        />
                    )}

                    {submitError && !(submitError)?.includes("Username") && (
                        <Typography fontSize={12} color="error" mt={2} sx={{ whiteSpace: "pre-wrap" }}>
                            {submitError}
                        </Typography>
                    )}

                    <Box
                        display="flex"
                        justifyContent="space-between"
                        mt={4}
                        sx={{
                            position: "sticky",
                            bottom: 0,
                            bgcolor: "#fff",
                            py: 2,
                            mx: { xs: -2.5, sm: -5 },
                            px: { xs: 2.5, sm: 5 },
                            borderTop: "1px solid #eceff1",
                            zIndex: 2,
                        }}
                    >
                        <Box>
                            {activeStep > 0 && (
                                <Button
                                    onClick={handlePrev}
                                    disabled={creatingUser || stepBusy}
                                    startIcon={<ArrowBack fontSize="small" />}
                                    sx={{ textTransform: "none", color: "#555" }}
                                >
                                    Previous
                                </Button>
                            )}
                        </Box>
                        <Box display="flex" gap={1.5}>
                            {activeStep < STEPS.length - 1 ? (
                                <Button
                                    onClick={handleNext}
                                    disabled={creatingUser || stepBusy}
                                    endIcon={(creatingUser || stepBusy) ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <ArrowForward fontSize="small" />}
                                    variant="contained"
                                    sx={{
                                        textTransform: "none", fontWeight: 600, borderRadius: 2,
                                        background: "linear-gradient(90deg, #FF2D92, #A855F7)",
                                        "&:hover": { background: "linear-gradient(90deg, #E0227D, #9333EA)" },
                                    }}
                                >
                                    {creatingUser ? "Creating..." : stepBusy ? stepBusyLabel : "Next"}
                                </Button>
                            ) : (
                                <Button
                                    onClick={onFinalSubmit}
                                    disabled={submitting || promptLoading}
                                    variant="contained"
                                    sx={{
                                        textTransform: "none", fontWeight: 600, borderRadius: 2, minWidth: 120,
                                        background: "linear-gradient(90deg, #FF2D92, #A855F7)",
                                        "&:hover": { background: "linear-gradient(90deg, #E0227D, #9333EA)" },
                                    }}
                                >
                                    {submitting ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Finish"}
                                </Button>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Box>
        </FormProvider>
    );
}